const ENCRYPTION_KEY = 'pbs-mysite-user-encryption-key-2026'; // In production, use environment variable
const COOKIE_NAME = 'pbs_original_user';
const COOKIE_MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export interface CookieOptions {
  maxAge?: number;
  path?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

/**
 * Simple XOR-based encryption for cookie data
 * Note: This provides obfuscation, not cryptographic security
 * For production, consider using a proper encryption library
 */
function simpleEncrypt(text: string, key: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(text.charCodeAt(i) ^ key.charCodeAt(i % key.length));
  }
  return btoa(result); // Base64 encode
}

/**
 * Simple XOR-based decryption
 */
function simpleDecrypt(encrypted: string, key: string): string {
  try {
    const decoded = atob(encrypted); // Base64 decode
    let result = '';
    for (let i = 0; i < decoded.length; i++) {
      result += String.fromCharCode(decoded.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }
    return result;
  } catch (error) {
    console.error('Error decrypting data:', error);
    return '';
  }
}

/**
 * Encrypts data
 */
export function encryptData(data: any): string {
  const jsonString = JSON.stringify(data);
  return simpleEncrypt(jsonString, ENCRYPTION_KEY);
}

/**
 * Decrypts encrypted data
 */
export function decryptData(encryptedData: string): any {
  try {
    const decryptedString = simpleDecrypt(encryptedData, ENCRYPTION_KEY);
    if (!decryptedString) return null;
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error('Error decrypting data:', error);
    return null;
  }
}

/**
 * Sets an encrypted cookie
 */
export function setEncryptedCookie(name: string, value: any, options?: CookieOptions): void {
  const encrypted = encryptData(value);
  const opts: CookieOptions = {
    maxAge: COOKIE_MAX_AGE,
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
    ...options
  };

  let cookieString = `${name}=${encodeURIComponent(encrypted)}`;
  
  if (opts.maxAge) {
    const expires = new Date(Date.now() + opts.maxAge).toUTCString();
    cookieString += `; expires=${expires}`;
  }
  
  if (opts.path) {
    cookieString += `; path=${opts.path}`;
  }
  
  if (opts.secure) {
    cookieString += '; secure';
  }
  
  if (opts.sameSite) {
    cookieString += `; samesite=${opts.sameSite}`;
  }

  document.cookie = cookieString;
}

/**
 * Gets and decrypts a cookie
 */
export function getEncryptedCookie(name: string): any {
  const nameEQ = name + '=';
  const cookies = document.cookie.split(';');
  
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(nameEQ) === 0) {
      const encryptedValue = decodeURIComponent(cookie.substring(nameEQ.length));
      return decryptData(encryptedValue);
    }
  }
  
  return null;
}

/**
 * Deletes a cookie
 */
export function deleteEncryptedCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

/**
 * Stores the original user in an encrypted cookie
 */
export function storeOriginalUser(user: any): void {
  setEncryptedCookie(COOKIE_NAME, user);
}

/**
 * Retrieves the original user from encrypted cookie
 */
export function getOriginalUser(): any {
  return getEncryptedCookie(COOKIE_NAME);
}

/**
 * Clears the original user cookie
 */
export function clearOriginalUser(): void {
  deleteEncryptedCookie(COOKIE_NAME);
}
